import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { GapFilterBanner } from '@/components/repGaps/GapFilterBanner'
import { repGapFor } from '@/lib/repGaps'
import {
  useOpportunities,
  useDeleteOpportunity,
  useBulkDeleteOpportunities,
  useBulkUpdateOpportunities,
  useBulkAssignOpportunities,
  useAccount,
  useProducts,
  useUpdateOpportunity,
  useUsers,
} from '@/hooks/use-api'
import { ownerPickerOptions } from '@/lib/salesReps'
import { apiClient } from '@/lib/api'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { DatePicker } from '@/components/ui/date-picker'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import {
  Target,
  DollarSign,
  TrendingUp,
  Download,
  Loader2,
  X,
  LayoutList,
  Columns,
  Scale,
  ChevronDown,
} from 'lucide-react'
import { formatCurrency, getFullFiscalYearRange, getCalendarYearRange } from '@/lib/utils'
import type { Opportunity } from '@/types'
import { exportOpportunitiesToCSV } from '@/lib/csv-export'
import { GRACKLE_COLORS } from '@/lib/theme'
import { OpportunityDetailModal } from '@/components/OpportunityDetailModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { BulkAssignModal } from '@/components/BulkAssignModal'
import {
  createOpportunitiesColumns,
  opportunitiesDefaultColumnSizing,
} from '@/config/columns/opportunities-columns'
import { ColumnPicker } from '@/components/ui/column-picker'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { useColumnOrder } from '@/hooks/use-column-order'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { useStages } from '@/hooks/use-picklists'
import { MobileOpportunityList } from '@/components/MobileOpportunityCard'
import { useMobile } from '@/hooks/use-mobile'
import { OpportunityKanban } from '@/components/OpportunityKanban'
import { useQueryClient } from '@tanstack/react-query'
import { explainError } from '@/lib/explain-error'

const fmtLocalDate = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const presetToCloseDateRange = (preset: string): { from: string; to: string } => {
  const today = new Date()
  switch (preset) {
    case 'this_month': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1)
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      return { from: fmtLocalDate(from), to: fmtLocalDate(to) }
    }
    case 'this_quarter': {
      const q = Math.floor(today.getMonth() / 3)
      const from = new Date(today.getFullYear(), q * 3, 1)
      const to = new Date(today.getFullYear(), q * 3 + 3, 0)
      return { from: fmtLocalDate(from), to: fmtLocalDate(to) }
    }
    case 'fiscal_year': {
      // GRM's fiscal year runs Mar 1 → end of Feb. Filters opps closing within
      // the current fiscal year (incl. future-dated opps still within FY).
      const { startDate, endDate } = getFullFiscalYearRange(today)
      return { from: startDate, to: endDate }
    }
    case 'calendar_year': {
      const { startDate, endDate } = getCalendarYearRange(today)
      return { from: startDate, to: endDate }
    }
    default:
      return { from: '', to: '' }
  }
}

const DEAL_ACTIVITY_LABELS: Record<string, string> = {
  stalled: 'Stalled, no activity in 30+ days',
  stalled_90: 'Stalled, no activity in 90+ days',
  no_next_step: 'No next step scheduled',
  stalled_90_no_next: 'No activity in 90+ days and nothing scheduled',
  via_account: 'Kept current only through account activity',
}

export default function Opportunities() {
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'opportunities')
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [showMyOpportunities, setShowMyOpportunities] = usePersistentState(fk('showMy'), !isAdmin)
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'close_date')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'desc')
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [opportunityToDelete, setOpportunityToDelete] = useState<{
    id: string
    name: string
  } | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isExporting, setIsExporting] = useState(false)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false)
  const isMobile = useMobile()
  const [selectedProductIds, setSelectedProductIds] = usePersistentState<string[]>(
    fk('productIds'),
    []
  )
  const { data: productsData } = useProducts({ limit: 10000, sort_by: 'name', sort_order: 'asc' })
  const allProducts = productsData?.data || []
  const { stages } = useStages()

  // View mode state (list or kanban)
  const [searchParams, setSearchParams] = useSearchParams()
  const view = (searchParams.get('view') === 'kanban' ? 'kanban' : 'list') as 'list' | 'kanban'

  // Kanban defaults state
  const [closeDateFrom, setCloseDateFrom] = useState<string | undefined>(undefined)
  const [closeDateTo, setCloseDateTo] = useState<string | undefined>(undefined)
  const [isClosedFilter, setIsClosedFilter] = useState<boolean | undefined>(undefined)

  // Owner scope from a dashboard deep-link (?owner_id=…). Overrides the My/All
  // toggle on arrival so the list matches the card; cleared when the user toggles.
  const [deepLinkOwnerId, setDeepLinkOwnerId] = useState<string | undefined>(undefined)

  // List-view close-date filter (independent of kanban auto-defaults)
  const [closeDatePreset, setCloseDatePreset] = usePersistentState<string>(
    fk('closeDatePreset'),
    ''
  )
  const [listCloseDateFrom, setListCloseDateFrom] = usePersistentState<string>(
    fk('listCloseDateFrom'),
    ''
  )
  const [listCloseDateTo, setListCloseDateTo] = usePersistentState<string>(
    fk('listCloseDateTo'),
    ''
  )
  // Multi-select stage filter. Persisted as an array; EMPTY = "All stages" (no
  // filter), matching the previous single-stage ''-means-all default and keeping
  // the default stable while the stage picklist (useStages) loads.
  const [stageList, setStageList] = usePersistentState<string[]>(fk('stages'), [])
  const selectedStages = useMemo(() => new Set(stageList), [stageList])

  // Track if kanban defaults have been applied
  const kanbanDefaultsAppliedRef = useRef(false)

  // Apply defaults when entering kanban mode
  useEffect(() => {
    if (view === 'kanban' && !kanbanDefaultsAppliedRef.current) {
      kanbanDefaultsAppliedRef.current = true
      const fmt = (d: Date) => {
        const year = d.getFullYear()
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      const today = new Date()
      const in9mo = new Date(today)
      in9mo.setMonth(today.getMonth() + 9)

      setShowMyOpportunities(true)
      setCloseDateFrom(fmt(today))
      setCloseDateTo(fmt(in9mo))
      setIsClosedFilter(false)
    }
    if (view === 'list') {
      kanbanDefaultsAppliedRef.current = false
    }
  }, [view])

  // Apply dashboard deep-link filters once: a stat-card click carries the
  // dashboard's My/All scope + date range so this list matches the card's
  // number. (status=open|won is read separately below for the banner + query.)
  const dashboardAppliedRef = useRef(false)
  useEffect(() => {
    if (dashboardAppliedRef.current) return
    const isDashboardLink =
      searchParams.has('owner_id') ||
      searchParams.has('my') ||
      searchParams.has('from') ||
      searchParams.has('to') ||
      searchParams.has('status') ||
      searchParams.has('activity') ||
      searchParams.has('gap')
    if (!isDashboardLink) return
    dashboardAppliedRef.current = true
    // Clear leftover ad-hoc filters so the deep-linked list reflects ONLY the
    // card's scope (a persisted stage/product filter would otherwise intersect
    // with ?status and could empty the list).
    setStageList([])
    setSelectedProductIds([])
    // Owner: explicit ?owner_id (any rep) wins; ?my back-compat → current user.
    const me = impersonatedUser?.id || currentUser?.id
    const ownerId = searchParams.get('owner_id') || (searchParams.has('my') ? me : undefined)
    setDeepLinkOwnerId(ownerId)
    setShowMyOpportunities(!!ownerId && ownerId === me)
    const from = searchParams.get('from') || ''
    const to = searchParams.get('to') || ''
    if (from || to) {
      setCloseDatePreset('custom')
      setListCloseDateFrom(from)
      setListCloseDateTo(to)
    } else {
      setCloseDatePreset('')
      setListCloseDateFrom('')
      setListCloseDateTo('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  // Only Admin/Super Admin can delete (single or bulk) — for cleanup
  const canDelete =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  // Only Admin/Super Admin can bulk-reassign the owner
  const canReassignOwner = canDelete

  // Read account filter from URL params
  const accountIdFilter = searchParams.get('accountId') || undefined
  // Dashboard stat deep-link: ?status=open (open pipeline, is_closed=false),
  // ?status=won (Closed Won), or ?status=forecast (Weighted Pipeline incl. Won —
  // every stage except Closed Lost: open + Service + Closed Won).
  const dealStatus = searchParams.get('status') || undefined
  // Exec Sales Dashboard drill-down: ?activity=stalled|stalled_90|no_next_step|via_account
  // (open deals only; the rule lives in backend/src/services/dealActivity.js).
  const dealActivityParam = searchParams.get('activity') || ''
  const dealActivity = dealActivityParam in DEAL_ACTIVITY_LABELS ? dealActivityParam : undefined
  // Exec Sales Dashboard "Follow-through" drill-down: ?gap=past_close|quote_unchased|...
  const repGap = repGapFor('opportunity', searchParams.get('gap'))

  // Shared by the list query and the CSV export, so an export always matches
  // exactly what the current filters show — not just the loaded page.
  const listParams = {
    search,
    page,
    owner_id:
      deepLinkOwnerId ??
      (showMyOpportunities ? impersonatedUser?.id || currentUser?.id : undefined),
    account_id: accountIdFilter,
    stage_name:
      stageList.length > 0 ? stageList.join(',') : dealStatus === 'won' ? 'Closed Won' : undefined,
    exclude_stages: dealStatus === 'forecast' ? 'Closed Lost' : undefined,
    activity: dealActivity,
    gap: repGap,
    product_ids: selectedProductIds.length > 0 ? selectedProductIds.join(',') : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    close_date_from: view === 'kanban' ? closeDateFrom : listCloseDateFrom || undefined,
    close_date_to: view === 'kanban' ? closeDateTo : listCloseDateTo || undefined,
    is_closed:
      view === 'kanban' && isClosedFilter !== undefined
        ? String(isClosedFilter)
        : dealStatus === 'open'
          ? 'false'
          : undefined,
  }

  const { data, isLoading } = useOpportunities(listParams)

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )

  const handleCloseDatePreset = useCallback(
    (preset: string) => {
      const next = closeDatePreset === preset ? '' : preset
      setCloseDatePreset(next)
      if (next && next !== 'custom') {
        const { from, to } = presetToCloseDateRange(next)
        setListCloseDateFrom(from)
        setListCloseDateTo(to)
      } else if (!next) {
        setListCloseDateFrom('')
        setListCloseDateTo('')
      }
      firstPage()
    },
    [closeDatePreset, firstPage]
  )

  const handleClearCloseDateFilter = useCallback(() => {
    setCloseDatePreset('')
    setListCloseDateFrom('')
    setListCloseDateTo('')
    firstPage()
  }, [firstPage])

  const allStageValues = useMemo(() => stages.map((s) => s.value), [stages])
  const toggleStage = useCallback(
    (value: string, checked: boolean) => {
      setStageList((prev) => {
        const effective = prev.length === 0 ? allStageValues : prev
        const next = checked
          ? Array.from(new Set([...effective, value]))
          : effective.filter((v) => v !== value)
        // Canonicalize "all selected" (or none) back to [] = All.
        return next.length === 0 || next.length >= allStageValues.length ? [] : next
      })
      firstPage()
    },
    [allStageValues, setStageList, firstPage]
  )

  const { data: filterAccount } = useAccount(accountIdFilter || '')
  const deleteMutation = useDeleteOpportunity()
  const bulkDeleteMutation = useBulkDeleteOpportunities()
  const bulkUpdateMutation = useBulkUpdateOpportunities()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const bulkAssignMutation = useBulkAssignOpportunities()
  const updateOppMutation = useUpdateOpportunity()
  const { toast } = useToast()

  // Handle stage change from Kanban drag-drop
  const handleStageChange = async (id: string, newStage: string) => {
    const queryKey = [
      'opportunities',
      {
        search,
        page,
        owner_id:
          deepLinkOwnerId ??
          (showMyOpportunities ? impersonatedUser?.id || currentUser?.id : undefined),
        account_id: accountIdFilter,
        product_ids: selectedProductIds.length > 0 ? selectedProductIds.join(',') : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        close_date_from: view === 'kanban' ? closeDateFrom : undefined,
        close_date_to: view === 'kanban' ? closeDateTo : undefined,
        is_closed:
          view === 'kanban' && isClosedFilter !== undefined ? String(isClosedFilter) : undefined,
      },
    ]

    // Snapshot current cache
    const previous = queryClient.getQueryData(queryKey)

    // Optimistic update
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!old?.data) return old
      return {
        ...old,
        data: old.data.map((o: any) => (o.id === id ? { ...o, stage_name: newStage } : o)),
      }
    })

    try {
      await updateOppMutation.mutateAsync({ id, data: { stage_name: newStage } })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    } catch (e) {
      // Rollback on error
      queryClient.setQueryData(queryKey, previous)
      const errorMessage =
        (e as any)?.response?.data?.error || (e as Error).message || 'Failed to update stage'
      toast({
        title: 'Failed to update stage',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const {
    items: opportunities,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
    stats,
  } = extract(data)

  const handleEditOpportunity = (opportunityId: string) => {
    setSelectedOpportunityId(opportunityId)
    setDetailModalOpen(true)
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!opportunityToDelete) return

    try {
      await deleteMutation.mutateAsync(opportunityToDelete.id)

      toast({
        title: 'Success',
        description: `Opportunity "${opportunityToDelete.name}" has been deleted.`,
      })

      setDeleteDialogOpen(false)
      setOpportunityToDelete(null)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete opportunity')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === opportunities.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(opportunities.map((opportunity) => opportunity.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} opportunit${idsArray.length === 1 ? 'y' : 'ies'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete opportunities')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} opportunit${selectedIds.size === 1 ? 'y' : 'ies'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update opportunities')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk assign
  const handleBulkAssign = async (ownerId: string, notifyOwner: boolean) => {
    try {
      await bulkAssignMutation.mutateAsync({
        ids: Array.from(selectedIds),
        owner_id: ownerId,
        notify_owner: notifyOwner,
      })

      toast({
        title: 'Success',
        description: `${selectedIds.size} opportunit${selectedIds.size === 1 ? 'y' : 'ies'} assigned successfully.`,
      })

      setBulkAssignModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to assign opportunities')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // CSV export: selected rows if any are checked, otherwise every record
  // matching the current filters (fetched in one request — the backend allows
  // limit up to 10,000 and the table peaks well below that).
  const handleExportCSV = async () => {
    if (selectedIds.size > 0) {
      const selected = opportunities.filter((o: Opportunity) => selectedIds.has(o.id))
      exportOpportunitiesToCSV(selected)
      toast({
        title: 'Export Successful',
        description: `Exported ${selected.length} selected opportunit${selected.length === 1 ? 'y' : 'ies'} to CSV`,
      })
      return
    }
    setIsExporting(true)
    try {
      const response = await apiClient.getOpportunities({ ...listParams, page: 1, limit: 10000 })
      const rows = response.data || []
      exportOpportunitiesToCSV(rows)
      toast({
        title: 'Export Successful',
        description: `Exported ${rows.length} opportunit${rows.length === 1 ? 'y' : 'ies'} to CSV`,
      })
    } catch (error) {
      toast({
        title: 'Export Failed',
        description:
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Could not fetch all opportunities for export',
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }

  // Define field configurations
  const opportunitiesUpdateFields: BulkUpdateField[] = [
    {
      key: 'stage_name',
      label: 'Stage',
      type: 'select',
      options: [
        { value: 'Prospecting', label: 'Prospecting' },
        { value: 'Qualification', label: 'Qualification' },
        { value: 'Proposal', label: 'Proposal' },
        { value: 'Negotiation', label: 'Negotiation' },
        { value: 'Closed Won', label: 'Closed Won' },
        { value: 'Closed Lost', label: 'Closed Lost' },
      ],
    },
    {
      key: 'probability',
      label: 'Probability (%)',
      type: 'number',
      placeholder: 'Enter probability',
    },
    { key: 'lead_source', label: 'Lead Source', type: 'text', placeholder: 'Enter lead source' },
    ...(canReassignOwner
      ? [
          {
            key: 'owner_id',
            label: 'Owner',
            type: 'select' as const,
            options: ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name })),
          },
        ]
      : []),
  ]

  // Create columns with callback
  const columns = useMemo(
    () =>
      createOpportunitiesColumns({
        onEditOpportunity: handleEditOpportunity,
        // Delete is offered to any non-impersonating user; the column shows it per-row only for
        // opps the user owns (or all rows for admins). Server enforces the same ownership rule.
        onDeleteOpportunity: !impersonatedUser
          ? (opportunityId: string, name: string) => {
              setOpportunityToDelete({ id: opportunityId, name })
              setDeleteDialogOpen(true)
            }
          : undefined,
        currentUserId: currentUser?.id,
        isAdmin,
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === opportunities.length && opportunities.length > 0,
      }),
    [selectedIds, opportunities.length, impersonatedUser, currentUser?.id, isAdmin]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(
    'opportunities-column-visibility'
  )
  const [columnOrder, setColumnOrder] = useColumnOrder('opportunities-column-order')

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="opportunity"
          onClearSelection={handleClearSelection}
          onBulkDelete={canDelete ? () => setBulkDeleteDialogOpen(true) : undefined}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          onBulkAssign={() => setBulkAssignModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
          isAssigning={bulkAssignMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="opportunity" />
            <h1 className="text-3xl font-bold tracking-tight">Opportunities</h1>
          </div>
          <p className="text-muted-foreground mt-1">Track and manage your sales opportunities</p>
          {accountIdFilter && filterAccount && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                Filtered by:{' '}
                <Link to={`/accounts/${filterAccount.id}`} className="font-medium hover:underline">
                  {filterAccount.name}
                </Link>
              </span>
              <Link
                to="/opportunities"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Clear filter
              </Link>
            </div>
          )}
          <GapFilterBanner
            gap={repGap}
            clearTo="/opportunities"
            ownerName={
              deepLinkOwnerId
                ? (users.find((u) => u.id === deepLinkOwnerId)?.name ?? 'selected rep')
                : undefined
            }
          />
          {dealActivity && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                Filtered by: {DEAL_ACTIVITY_LABELS[dealActivity]}
                {deepLinkOwnerId
                  ? ` · ${users.find((u) => u.id === deepLinkOwnerId)?.name ?? 'selected rep'}`
                  : ''}
              </span>
              <Link
                to="/opportunities"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Clear filter
              </Link>
            </div>
          )}
          {dealStatus && ['open', 'won', 'forecast'].includes(dealStatus) && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
                Filtered by:{' '}
                {dealStatus === 'open'
                  ? 'Open opportunities'
                  : dealStatus === 'won'
                    ? 'Won opportunities'
                    : 'Weighted pipeline (incl. Won & Service)'}
              </span>
              <Link
                to="/opportunities"
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Clear filter
              </Link>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['opportunities']} label="opportunities" />
          {canExport && (
            <Button
              variant="outline"
              aria-label={
                selectedIds.size > 0
                  ? `Export ${selectedIds.size} selected opportunities to CSV`
                  : 'Export all filtered opportunities to CSV'
              }
              onClick={handleExportCSV}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {selectedIds.size > 0 ? `Export (${selectedIds.size})` : 'Export'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Opportunities</CardTitle>
            <Target className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{total}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {opportunities.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Value (CAD)</CardTitle>
            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(stats?.totalValue ?? 0, 'CAD')}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Weighted Value (CAD)</CardTitle>
            <Scale className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {formatCurrency(stats?.weightedValue ?? 0, 'CAD')}
            </div>
            <p className="text-xs text-muted-foreground leading-tight">Probability-adjusted</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Avg Probability</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {(stats?.avgProbability ?? 0).toFixed(0)}%
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Closed Won</CardTitle>
            <Target className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.closedWon ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{showMyOpportunities ? 'My Opportunities' : 'All Opportunities'}</CardTitle>
          <CardDescription>View and manage your sales pipeline</CardDescription>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Tabs
              defaultValue="all"
              value={showMyOpportunities ? 'my' : 'all'}
              onValueChange={(value) => {
                setDeepLinkOwnerId(undefined)
                setShowMyOpportunities(value === 'my')
                firstPage()
              }}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="Show all opportunities">
                  All Opportunities
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my opportunities">
                  My Opportunities
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div
              role="group"
              aria-label="View mode"
              className="inline-flex rounded-md border bg-background"
            >
              <Button
                variant={view === 'list' ? 'default' : 'ghost'}
                size="sm"
                aria-pressed={view === 'list'}
                onClick={() =>
                  setSearchParams((prev) => {
                    const np = new URLSearchParams(prev)
                    np.set('view', 'list')
                    return np
                  })
                }
                className="rounded-r-none"
              >
                <LayoutList className="mr-1.5 h-4 w-4" aria-hidden="true" />
                List
              </Button>
              <Button
                variant={view === 'kanban' ? 'default' : 'ghost'}
                size="sm"
                aria-pressed={view === 'kanban'}
                onClick={() =>
                  setSearchParams((prev) => {
                    const np = new URLSearchParams(prev)
                    np.set('view', 'kanban')
                    return np
                  })
                }
                className="rounded-l-none"
              >
                <Columns className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Kanban
              </Button>
            </div>
            {view === 'list' && (
              <>
                <SearchWithRecent
                  value={search}
                  onChange={setSearch}
                  placeholder="Search opportunities..."
                  ariaLabel="Search opportunities"
                  entityKey="opportunities"
                />
                <div className="relative">
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value && !selectedProductIds.includes(e.target.value)) {
                        setSelectedProductIds((prev) => [...prev, e.target.value])
                        firstPage()
                      }
                      e.target.value = ''
                    }}
                    className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Filter by product"
                  >
                    <option value="">Filter by Product...</option>
                    {allProducts
                      .filter((p: any) => !selectedProductIds.includes(p.id))
                      .map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-48 justify-between"
                      aria-label="Filter by stage"
                    >
                      {stageList.length === 0 ? 'All Stages' : `Stage (${stageList.length})`}
                      <ChevronDown className="ml-2 h-4 w-4 opacity-50" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
                    <DropdownMenuLabel>Filter by Stage</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {stages.map((s) => (
                      <DropdownMenuCheckboxItem
                        key={s.value}
                        checked={stageList.length === 0 || selectedStages.has(s.value)}
                        onCheckedChange={(checked) => toggleStage(s.value, checked === true)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {s.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuCheckboxItem
                      checked={stageList.length === 0}
                      onCheckedChange={() => {
                        setStageList([])
                        firstPage()
                      }}
                      onSelect={(e) => e.preventDefault()}
                    >
                      Select All
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <ColumnPicker
                  columns={columns}
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={setColumnVisibility}
                  columnOrder={columnOrder}
                  onColumnOrderChange={setColumnOrder}
                />
              </>
            )}
          </div>
          {view === 'list' && (
            <div
              className="flex flex-wrap items-center gap-2 pt-2"
              role="toolbar"
              aria-label="Filter opportunities by close date"
            >
              <span className="text-sm text-muted-foreground mr-1">Close date:</span>
              {[
                { value: 'this_month', label: 'This Month' },
                { value: 'this_quarter', label: 'This Quarter' },
                { value: 'fiscal_year', label: 'This Fiscal Year' },
                { value: 'calendar_year', label: 'This Calendar Year' },
                { value: 'custom', label: 'Custom' },
              ].map((f) => (
                <Button
                  key={f.value}
                  variant={closeDatePreset === f.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCloseDatePreset(f.value)}
                  aria-pressed={closeDatePreset === f.value}
                  aria-label={`Filter by close date ${f.label}`}
                >
                  {f.label}
                </Button>
              ))}
              {closeDatePreset === 'custom' && (
                <div className="flex items-center gap-2">
                  <DatePicker
                    value={listCloseDateFrom}
                    onChange={(v) => {
                      setListCloseDateFrom(v ?? '')
                      firstPage()
                    }}
                    className="w-36 h-8"
                    aria-label="Close date from"
                  />
                  <span className="text-sm text-muted-foreground">to</span>
                  <DatePicker
                    value={listCloseDateTo}
                    onChange={(v) => {
                      setListCloseDateTo(v ?? '')
                      firstPage()
                    }}
                    className="w-36 h-8"
                    aria-label="Close date to"
                  />
                </div>
              )}
              {closeDatePreset && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearCloseDateFilter}
                  aria-label="Clear close date filter"
                >
                  <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                  Clear
                </Button>
              )}
            </div>
          )}
          {selectedProductIds.length > 0 && (
            <div
              className="flex flex-wrap items-center gap-2 mt-3 p-2 border rounded-md"
              style={{
                backgroundColor: GRACKLE_COLORS.paleBlue,
                borderColor: GRACKLE_COLORS.powderBlue,
              }}
            >
              <span className="text-sm font-medium" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                Filtered by product:
              </span>
              {selectedProductIds.map((pid) => {
                const product = allProducts.find((p: any) => p.id === pid)
                return (
                  <span
                    key={pid}
                    className="inline-flex items-center gap-1 text-sm font-medium px-2.5 py-0.5 rounded-full"
                    style={{
                      backgroundColor: GRACKLE_COLORS.pollen,
                      color: GRACKLE_COLORS.prussianBlue,
                    }}
                  >
                    {product?.name || pid}
                    <button
                      onClick={() => {
                        setSelectedProductIds((prev) => prev.filter((id) => id !== pid))
                        firstPage()
                      }}
                      className="rounded-full p-0.5 focus:outline-none focus:ring-2 focus:ring-white hover:opacity-80"
                      aria-label={`Remove ${product?.name || 'product'} filter`}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                )
              })}
              <button
                onClick={() => {
                  setSelectedProductIds([])
                  firstPage()
                }}
                className="text-sm font-medium underline ml-1 hover:opacity-75"
                style={{ color: GRACKLE_COLORS.prussianBlue }}
                aria-label="Clear all product filters"
              >
                Clear all
              </button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {view === 'kanban' ? (
            <OpportunityKanban
              opportunities={opportunities}
              onStageChange={handleStageChange}
              isLoading={isLoading}
            />
          ) : (
            <>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading opportunities...
                </div>
              ) : opportunities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No opportunities found. Create your first opportunity to get started.
                </div>
              ) : isMobile ? (
                <MobileOpportunityList
                  opportunities={opportunities}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  onEdit={(opportunity) => handleEditOpportunity(opportunity.id)}
                  onDelete={(opportunity) => {
                    setOpportunityToDelete({ id: opportunity.id, name: opportunity.name ?? '' })
                    setDeleteDialogOpen(true)
                  }}
                />
              ) : (
                <ResizableTable
                  columns={columns}
                  data={opportunities}
                  storageKey="opportunities-column-widths"
                  defaultColumnSizing={opportunitiesDefaultColumnSizing}
                  columnVisibility={columnVisibility}
                  columnOrder={columnOrder}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  onColumnVisibilityChange={setColumnVisibility}
                />
              )}

              {/* Pagination */}
              {!isLoading && total > limit && (
                <div className="flex items-center justify-between px-2 py-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * limit + 1} to{' '}
                    {Math.min(currentPage * limit, total)} of {total} opportunities
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                      Previous
                    </Button>
                    <div className="flex items-center gap-1 text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <OpportunityDetailModal
        opportunityId={selectedOpportunityId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-opportunity-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Opportunity</AlertDialogTitle>
            <AlertDialogDescription id="delete-opportunity-description">
              Are you sure you want to delete the opportunity "{opportunityToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete opportunity ${opportunityToDelete?.name}`}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="opportunity"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="opportunity"
        fields={opportunitiesUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />

      <BulkAssignModal
        open={bulkAssignModalOpen}
        onOpenChange={setBulkAssignModalOpen}
        selectedCount={selectedIds.size}
        entityType="opportunity"
        onConfirm={handleBulkAssign}
        isAssigning={bulkAssignMutation.isPending}
      />
    </div>
  )
}
